import { useQuery } from '@apollo/client';
import { GET_CREATIVE, GET_CREATIVE_PUBLIC, CREATIVES_BY_USER_ID, STUDIO_CREATIVES_BY_STUDIO_ID } from '../../graphql/queries/creativeQueries';
import { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getCloudFrontImageUrl, getDirectS3StudioImageUrl } from '../../util/cloudFrontUtils';
import awsConfig from '../../config/aws-config';


export const useStudioHasCreatives = (studioId) => {
  const { data, loading, error } = useQuery(STUDIO_CREATIVES_BY_STUDIO_ID, {
    variables: { studioID: studioId },
    skip: !studioId,
    fetchPolicy: 'cache-and-network',
  });
  return {
    hasCreatives: data?.studioCreativesByStudioID?.items?.length > 0,
    loading,
    error
  };
};


/**
 * Hook to fetch a single Creative profile by ID
 * Use this on a Creative Detail Page (direct link)
 * @param {string} creativeId - The Creative ID
 * @returns {object} - { creative, loading, error }
 */
export const useCreative = (creativeId) => {
  const { data, loading, error } = useQuery(GET_CREATIVE, {
    variables: { id: creativeId },
    skip: !creativeId,
    fetchPolicy: 'cache-and-network',
  });

  const processedCreative = useMemo(() => {
    if (!data?.getCreative) return null;

    const creative = { ...data.getCreative };

    // Process profile picture
    if (creative.profilePicture) {
      let processedProfilePicture = "";
      try {
        processedProfilePicture = getCloudFrontImageUrl(creative.profilePicture, {
          width: 300,
          height: 300,
          fit: 'cover'
        });
      } catch (error) {
        try {
          const cleanKey = creative.profilePicture.startsWith('public/') 
            ? creative.profilePicture 
            : `public/${creative.profilePicture}`;
          processedProfilePicture = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
        } catch (fallbackError) {
          processedProfilePicture = "";
        }
      }
      creative.processedProfilePicture = processedProfilePicture;
    }

    // Process user profile picture if exists
    if (creative.user?.profilePicture) {
      let processedUserPicture = "";
      try {
        processedUserPicture = getCloudFrontImageUrl(creative.user.profilePicture, {
          width: 200,
          height: 200,
          fit: 'cover'
        });
      } catch (error) {
        processedUserPicture = "";
      }
      creative.user = {
        ...creative.user,
        processedProfilePicture: processedUserPicture
      };
    }

    // Process portfolio images in packages
    if (creative.packages?.items) {
      creative.packages = {
        ...creative.packages,
        items: creative.packages.items.map(pkg => {
          const processedPkg = { ...pkg };
          if (pkg.portfolioImages && Array.isArray(pkg.portfolioImages)) {
            processedPkg.processedPortfolioImages = pkg.portfolioImages.map(imageKey => {
              if (!imageKey) return '';
              try {
                return getCloudFrontImageUrl(imageKey, {
                  width: 800,
                  height: 600,
                  fit: 'cover'
                });
              } catch (error) {
                try {
                  const cleanKey = imageKey.startsWith('public/') ? imageKey : `public/${imageKey}`;
                  return `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                } catch (fallbackError) {
                  return '';
                }
              }
            });
          }
          return processedPkg;
        })
      };
    }

    // Process project images and audio files
    if (creative.projects?.items) {
      const allProcessedImages = [];
      creative.projects = {
        ...creative.projects,
        items: creative.projects.items.map(project => {
          const processedProject = { ...project };
          
          // Process images
          if (project.images && Array.isArray(project.images)) {
            processedProject.processedImages = project.images.map(img => {
              if (typeof img === 'string') {
                // Simple string URL - only process if it looks like an image (not video or external link)
                const isExternalLink = img.includes('youtube.com') || img.includes('youtu.be') || img.includes('vimeo.com');
                const isVideoFile = /\.(mp4|mov|avi|wmv|flv|webm|mkv|m4v)$/i.test(img);

                if (isExternalLink || isVideoFile) {
                  const processedImg = { url: img, processedUrl: img };
                  allProcessedImages.push(processedImg);
                  return img;
                }

                try {
                  const processedUrl = getCloudFrontImageUrl(img, {
                    width: 1200,
                    height: 800,
                    fit: 'cover'
                  });
                  const processedImg = { url: img, processedUrl };
                  allProcessedImages.push(processedImg);
                  return processedUrl;
                } catch (error) {
                  const processedImg = { url: img, processedUrl: img };
                  allProcessedImages.push(processedImg);
                  return img;
                }
              } else if (img?.url) {
                // ProjectImage object with url, caption, thumbnailUrl
                const processedImg = { ...img };
                const url = img.url;
                const isExternalLink = typeof url === 'string' && (
                  url.includes('youtube.com') ||
                  url.includes('youtu.be') ||
                  url.includes('vimeo.com')
                );
                const isVideoFile = typeof url === 'string' && /\.(mp4|mov|avi|wmv|flv|webm|mkv|m4v)$/i.test(url);

                // Only send real images through CloudFront image handler
                if (!isExternalLink && !isVideoFile && url) {
                  try {
                    processedImg.processedUrl = getCloudFrontImageUrl(url, {
                      width: 1200,
                      height: 800,
                      fit: 'cover'
                    });
                  } catch (error) {
                    processedImg.processedUrl = url;
                  }
                } else {
                  processedImg.processedUrl = url;
                }

                // For any item with a thumbnail (YouTube or uploaded video), process thumbnail as image
                if (img.thumbnailUrl) {
                  try {
                    processedImg.processedThumbnailUrl = getCloudFrontImageUrl(img.thumbnailUrl, {
                      width: 400,
                      height: 300,
                      fit: 'cover'
                    });
                  } catch (error) {
                    processedImg.processedThumbnailUrl = img.thumbnailUrl;
                  }
                }

                allProcessedImages.push(processedImg);
                return processedImg;
              }
              return img;
            });
          }

          // Process audio files (for MUSIC type projects)
          if (project.audios && Array.isArray(project.audios)) {
            processedProject.audios = project.audios.map(audio => {
              const processedAudio = { ...audio };
              
              // Store the clean key for audio URL resolution
              // Components should use getUrl from AWS Amplify to resolve the URL asynchronously
              // Note: We don't generate URLs here because getUrl is async and useMemo is sync
              if (audio.url) {
                // The key from uploadData with accessLevel: 'public' already includes 'public/' prefix
                // So we should use it as-is, but normalize it to ensure it has the prefix
                // Remove any duplicate 'public/' prefixes first
                let cleanKey = audio.url;
                while (cleanKey.startsWith('public/public/')) {
                  cleanKey = cleanKey.replace('public/public/', 'public/');
                }
                // Ensure it has the prefix (in case it doesn't for some reason)
                if (!cleanKey.startsWith('public/')) {
                  cleanKey = `public/${cleanKey}`;
                }
                processedAudio.audioKey = cleanKey;
                // Store original URL as fallback
                processedAudio.originalUrl = audio.url;
              }
              
              // Process cover image for audio
              if (audio.coverImage) {
                try {
                  processedAudio.processedCoverImage = getCloudFrontImageUrl(audio.coverImage, {
                    width: 600,
                    height: 600,
                    fit: 'cover'
                  });
                } catch (error) {
                  try {
                    const cleanKey = audio.coverImage.startsWith('public/') 
                      ? audio.coverImage 
                      : `public/${audio.coverImage}`;
                    processedAudio.processedCoverImage = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                  } catch (fallbackError) {
                    processedAudio.processedCoverImage = audio.coverImage;
                  }
                }
              }
              
              return processedAudio;
            });
          }
          
          return processedProject;
        })
      };
      // Add flattened array of all processed images for easy access
      creative.allProcessedProjectImages = allProcessedImages;
    }

    // Process studio images in linked studios
    if (creative.studios?.items) {
      creative.studios = {
        ...creative.studios,
        items: creative.studios.items.map(studioLink => {
          const processedStudioLink = { ...studioLink };
          if (studioLink.studio?.images && Array.isArray(studioLink.studio.images)) {
            processedStudioLink.studio = {
              ...studioLink.studio,
              processedImages: studioLink.studio.images.map(imageKey => {
                if (!imageKey) return '';
                try {
                  return getCloudFrontImageUrl(imageKey, {
                    width: 600,
                    height: 450,
                    fit: 'cover'
                  });
                } catch (error) {
                  return '';
                }
              })
            };
          }
          return processedStudioLink;
        })
      };
    }

    return creative;
  }, [data]);

  return {
    creative: processedCreative,
    loading,
    error
  };
};

/**
 * Hook to fetch all Creatives for a specific user
 * Use this on User Account (own profile)
 * @param {string} userId - The User ID
 * @returns {object} - { creativesData: { creatives, hasCreatives }, loading, error }
 */
export const useCreativesByUserId = (userId) => {
  const { data, loading, error } = useQuery(CREATIVES_BY_USER_ID, {
    variables: { 
      userID: userId,
      limit: 100
    },
    skip: !userId,
    // List view must reconcile with the server so project thumbnails match
    // the creative detail page (cache-first can keep stale or partial data).
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const processedData = useMemo(() => {
    if (!data?.creativesByUserID?.items) return null;

    const creatives = data.creativesByUserID.items.map(creative => {
      const processedCreative = { ...creative };

      // Process profile picture (smaller + lower quality for faster list loading)
      if (processedCreative.profilePicture) {
        let processedProfilePicture = "";
        try {
          processedProfilePicture = getCloudFrontImageUrl(processedCreative.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover',
            quality: 70,
          }) || getDirectS3StudioImageUrl(processedCreative.profilePicture);
        } catch (error) {
          processedProfilePicture = getDirectS3StudioImageUrl(processedCreative.profilePicture) || "";
        }
        processedCreative.processedProfilePicture = processedProfilePicture;
      }

      // Process user profile picture
      if (processedCreative.user?.profilePicture) {
        let processedUserPicture = "";
        try {
          processedUserPicture = getCloudFrontImageUrl(processedCreative.user.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover'
          });
        } catch (error) {
          processedUserPicture = "";
        }
        processedCreative.user = {
          ...processedCreative.user,
          processedProfilePicture: processedUserPicture
        };
      }

      // Process project images and audio (for MUSIC) - same structure as useCreative
      if (processedCreative.projects?.items) {
        const allProcessedImages = [];
        const allProcessedAudios = [];
        processedCreative.projects = {
          ...processedCreative.projects,
          items: processedCreative.projects.items.map(project => {
            const processedProject = { ...project };
            if (project.images && Array.isArray(project.images)) {
              processedProject.processedImages = project.images.map(img => {
                if (typeof img === 'string') {
                  const isExternalLink = img.includes('youtube.com') || img.includes('youtu.be') || img.includes('vimeo.com');
                  const isVideoFile = /\.(mp4|mov|avi|wmv|flv|webm|mkv|m4v)$/i.test(img);

                  if (isExternalLink || isVideoFile) {
                    const processedImg = { url: img, processedUrl: img };
                    allProcessedImages.push(processedImg);
                    return img;
                  }

                  try {
                    // Use a smaller size + slightly lower quality for overview grids
                    let processedUrl = getCloudFrontImageUrl(img, {
                      width: 800,
                      height: 600,
                      fit: 'cover',
                      quality: 70,
                    }) || '';
                    if (!processedUrl) {
                      processedUrl = getDirectS3StudioImageUrl(img) || img;
                    }
                    const processedImg = { url: img, processedUrl };
                    allProcessedImages.push(processedImg);
                    return processedUrl;
                  } catch (error) {
                    const fallback = getDirectS3StudioImageUrl(img) || img;
                    const processedImg = { url: img, processedUrl: fallback };
                    allProcessedImages.push(processedImg);
                    return fallback;
                  }
                } else if (img?.url) {
                  const processedImg = { ...img };
                  const url = img.url;
                  const isExternalLink = typeof url === 'string' && (
                    url.includes('youtube.com') ||
                    url.includes('youtu.be') ||
                    url.includes('vimeo.com')
                  );
                  const isVideoFile = typeof url === 'string' && /\.(mp4|mov|avi|wmv|flv|webm|mkv|m4v)$/i.test(url);

                  if (!isExternalLink && !isVideoFile && url) {
                    try {
                      // Slightly smaller + lower quality thumbnails for lists
                      processedImg.processedUrl = getCloudFrontImageUrl(url, {
                        width: 700,
                        height: 500,
                        fit: 'cover',
                        quality: 70,
                      }) || '';
                      if (!processedImg.processedUrl) {
                        processedImg.processedUrl = getDirectS3StudioImageUrl(url) || url;
                      }
                    } catch (error) {
                      try {
                        processedImg.processedUrl = getDirectS3StudioImageUrl(url) || `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${url.startsWith('public/') ? url : `public/${url}`}`;
                      } catch (fallbackError) {
                        processedImg.processedUrl = url;
                      }
                    }
                  } else {
                    processedImg.processedUrl = url;
                  }

                  if (img.thumbnailUrl) {
                    try {
                      processedImg.processedThumbnailUrl = getCloudFrontImageUrl(img.thumbnailUrl, {
                        width: 400,
                        height: 300,
                        fit: 'cover'
                      }) || getDirectS3StudioImageUrl(img.thumbnailUrl);
                    } catch (error) {
                      processedImg.processedThumbnailUrl = img.thumbnailUrl;
                    }
                    if (!processedImg.processedThumbnailUrl) {
                      processedImg.processedThumbnailUrl = getDirectS3StudioImageUrl(img.thumbnailUrl) || img.thumbnailUrl;
                    }
                  }

                  allProcessedImages.push(processedImg);
                  return processedImg;
                }
                return img;
              });
            }
            // Process audio files (for MUSIC type projects) - same as useCreative
            if (project.audios && Array.isArray(project.audios)) {
              processedProject.audios = project.audios.map(audio => {
                const processedAudio = { ...audio };
                if (audio.url) {
                  let cleanKey = audio.url;
                  while (cleanKey.startsWith('public/public/')) {
                    cleanKey = cleanKey.replace('public/public/', 'public/');
                  }
                  if (!cleanKey.startsWith('public/')) {
                    cleanKey = `public/${cleanKey}`;
                  }
                  processedAudio.audioKey = cleanKey;
                  processedAudio.originalUrl = audio.url;
                }
                if (audio.coverImage) {
                  try {
                    processedAudio.processedCoverImage = getCloudFrontImageUrl(audio.coverImage, {
                      width: 600,
                      height: 600,
                      fit: 'cover'
                    }) || getDirectS3StudioImageUrl(audio.coverImage);
                  } catch (error) {
                    try {
                      const cleanKey = audio.coverImage.startsWith('public/')
                        ? audio.coverImage
                        : `public/${audio.coverImage}`;
                      processedAudio.processedCoverImage = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                    } catch (fallbackError) {
                      processedAudio.processedCoverImage = audio.coverImage;
                    }
                  }
                  if (!processedAudio.processedCoverImage) {
                    processedAudio.processedCoverImage = getDirectS3StudioImageUrl(audio.coverImage) || '';
                  }
                }
                if (processedCreative.creativeType === 'MUSIC') {
                  allProcessedAudios.push(processedAudio);
                }
                return processedAudio;
              });
            }
            return processedProject;
          })
        };
        processedCreative.allProcessedProjectImages = allProcessedImages;
        if (processedCreative.creativeType === 'MUSIC') {
          processedCreative.allProcessedProjectAudios = allProcessedAudios;
        }
      }

      // Ensure canBePublished is a boolean (handle null from backend)
      // Backend should set this, but if it's null, default to false
      if (processedCreative.canBePublished === null || processedCreative.canBePublished === undefined) {
        // If backend hasn't set it yet, check manually as fallback
        // Check if there are active packages and active projects
        const hasActivePackages = processedCreative.packages?.items?.some(p => p.isActive === true) ?? false;
        const hasActiveProjects = processedCreative.projects?.items?.some(p => p.status === 'PUBLISHED') ?? false;
        processedCreative.canBePublished = hasActivePackages && hasActiveProjects;
      }

      return processedCreative;
    });

    return {
      creatives: creatives,
      hasCreatives: creatives.length > 0
    };
  }, [data]);

  return {
    creativesData: processedData,
    loading,
    error
  };
};

/**
 * Hook to fetch all PUBLISHED Creatives linked to a Studio
 * This hook automatically filters out DRAFT and INACTIVE creatives
 * Use this on a Studio Detail Page to show only active creatives
 * @param {string} studioId - The Studio ID
 * @returns {object} - { creatives, loading, error }
 */
/**
 * Hook to fetch a Creative profile for public MemberPage display
 * Only returns PUBLISHED creatives, unless the current user is the owner
 * Use this on MemberPage (public-facing creative profile pages)
 * @param {string} creativeId - The Creative ID
 * @returns {object} - { creative, loading, error, isNotFound }
 */
export const useCreativeMemberPage = (creativeId) => {
  const { user: currentUser } = useSelector((state) => state.auth || {});
  const currentUserId = currentUser?._id || currentUser?.id;

  // Use GET_CREATIVE_PUBLIC which doesn't request email/phoneNumber (protected fields)
  // This prevents authorization errors on public member pages
  const { data, loading, error } = useQuery(GET_CREATIVE_PUBLIC, {
    variables: { id: creativeId },
    skip: !creativeId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all', // Continue even if there are errors
  });

  const processedCreative = useMemo(() => {
    if (!data?.getCreative) return null;

    const creative = { ...data.getCreative };

    // Check if current user is the owner
    const isOwner = currentUserId && creative.userID && currentUserId === creative.userID;

    // Only allow access if PUBLISHED or if user is the owner
    if (creative.status !== 'PUBLISHED' && !isOwner) {
      return null; // Return null for non-published, non-owned creatives
    }

    // Process profile picture
    if (creative.profilePicture) {
      let processedProfilePicture = "";
      try {
        processedProfilePicture = getCloudFrontImageUrl(creative.profilePicture, {
          width: 300,
          height: 300,
          fit: 'cover'
        });
      } catch (error) {
        try {
          const cleanKey = creative.profilePicture.startsWith('public/') 
            ? creative.profilePicture 
            : `public/${creative.profilePicture}`;
          processedProfilePicture = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
        } catch (fallbackError) {
          processedProfilePicture = "";
        }
      }
      creative.processedProfilePicture = processedProfilePicture;
    }

    // Process user profile picture if exists
    if (creative.user?.profilePicture) {
      let processedUserPicture = "";
      try {
        processedUserPicture = getCloudFrontImageUrl(creative.user.profilePicture, {
          width: 200,
          height: 200,
          fit: 'cover'
        });
      } catch (error) {
        processedUserPicture = "";
      }
      creative.user = {
        ...creative.user,
        processedProfilePicture: processedUserPicture
      };
    }

    // Process portfolio images in packages
    if (creative.packages?.items) {
      creative.packages = {
        ...creative.packages,
        items: creative.packages.items.map(pkg => {
          const processedPkg = { ...pkg };
          if (pkg.portfolioImages && Array.isArray(pkg.portfolioImages)) {
            processedPkg.processedPortfolioImages = pkg.portfolioImages.map(imageKey => {
              if (!imageKey) return '';
              try {
                return getCloudFrontImageUrl(imageKey, {
                  width: 800,
                  height: 600,
                  fit: 'cover'
                });
              } catch (error) {
                try {
                  const cleanKey = imageKey.startsWith('public/') ? imageKey : `public/${imageKey}`;
                  return `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                } catch (fallbackError) {
                  return '';
                }
              }
            });
          }
          return processedPkg;
        })
      };
    }

    // Process project images and audio files
    if (creative.projects?.items) {
      const allProcessedImages = [];
      creative.projects = {
        ...creative.projects,
        items: creative.projects.items.map(project => {
          const processedProject = { ...project };
          
          // Process images
          if (project.images && Array.isArray(project.images)) {
            processedProject.processedImages = project.images.map(img => {
              if (typeof img === 'string') {
                // Simple string URL
                try {
                  const processedUrl = getCloudFrontImageUrl(img, {
                    width: 1200,
                    height: 800,
                    fit: 'cover'
                  });
                  const processedImg = { url: img, processedUrl };
                  allProcessedImages.push(processedImg);
                  return processedUrl;
                } catch (error) {
                  const processedImg = { url: img, processedUrl: img };
                  allProcessedImages.push(processedImg);
                  return img;
                }
              } else if (img?.url) {
                // ProjectImage object with url, caption, thumbnailUrl
                const processedImg = { ...img };
                try {
                  processedImg.processedUrl = getCloudFrontImageUrl(img.url, {
                    width: 1200,
                    height: 800,
                    fit: 'cover'
                  });
                  if (img.thumbnailUrl) {
                    processedImg.processedThumbnailUrl = getCloudFrontImageUrl(img.thumbnailUrl, {
                      width: 400,
                      height: 300,
                      fit: 'cover'
                    });
                  }
                } catch (error) {
                  processedImg.processedUrl = img.url;
                }
                allProcessedImages.push(processedImg);
                return processedImg;
              }
              return img;
            });
          }

          // Process audio files (for MUSIC type projects)
          if (project.audios && Array.isArray(project.audios)) {
            processedProject.audios = project.audios.map(audio => {
              const processedAudio = { ...audio };
              
              // Store the clean key for audio URL resolution
              // Components should use getUrl from AWS Amplify to resolve the URL asynchronously
              // Note: We don't generate URLs here because getUrl is async and useMemo is sync
              if (audio.url) {
                // The key from uploadData with accessLevel: 'public' already includes 'public/' prefix
                // So we should use it as-is, but normalize it to ensure it has the prefix
                // Remove any duplicate 'public/' prefixes first
                let cleanKey = audio.url;
                while (cleanKey.startsWith('public/public/')) {
                  cleanKey = cleanKey.replace('public/public/', 'public/');
                }
                // Ensure it has the prefix (in case it doesn't for some reason)
                if (!cleanKey.startsWith('public/')) {
                  cleanKey = `public/${cleanKey}`;
                }
                processedAudio.audioKey = cleanKey;
                // Store original URL as fallback
                processedAudio.originalUrl = audio.url;
              }
              
              // Process cover image for audio
              if (audio.coverImage) {
                try {
                  processedAudio.processedCoverImage = getCloudFrontImageUrl(audio.coverImage, {
                    width: 600,
                    height: 600,
                    fit: 'cover'
                  });
                } catch (error) {
                  try {
                    const cleanKey = audio.coverImage.startsWith('public/') 
                      ? audio.coverImage 
                      : `public/${audio.coverImage}`;
                    processedAudio.processedCoverImage = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                  } catch (fallbackError) {
                    processedAudio.processedCoverImage = audio.coverImage;
                  }
                }
              }
              
              return processedAudio;
            });
          }
          
          return processedProject;
        })
      };
      // Add flattened array of all processed images for easy access
      creative.allProcessedProjectImages = allProcessedImages;
    }

    // Process studio images in linked studios
    if (creative.studios?.items) {
      creative.studios = {
        ...creative.studios,
        items: creative.studios.items.map(studioLink => {
          const processedStudioLink = { ...studioLink };
          if (studioLink.studio?.images && Array.isArray(studioLink.studio.images)) {
            processedStudioLink.studio = {
              ...studioLink.studio,
              processedImages: studioLink.studio.images.map(imageKey => {
                if (!imageKey) return '';
                try {
                  return getCloudFrontImageUrl(imageKey, {
                    width: 600,
                    height: 450,
                    fit: 'cover'
                  });
                } catch (error) {
                  return '';
                }
              })
            };
          }
          return processedStudioLink;
        })
      };
    }

    return creative;
  }, [data, currentUserId]);

  // Determine if the creative was not found (either doesn't exist or is not accessible)
  const isNotFound = !loading && !error && !processedCreative;

  return {
    creative: processedCreative,
    loading,
    error: isNotFound ? new Error('Creative not found or not published') : error,
    isNotFound
  };
};

export const useStudioCreatives = (studioId) => {
  const { data, loading, error } = useQuery(STUDIO_CREATIVES_BY_STUDIO_ID, {
    variables: {
      studioID: studioId,
      limit: 100
    },
    skip: !studioId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all', // Continue even if there are errors
  });

  const processedData = useMemo(() => {
    if (!data?.studioCreativesByStudioID?.items) return null;

    // IMPORTANT: Filter for only PUBLISHED (active) creatives
    // This ensures only active creatives are displayed on the studio page
    // Note: GraphQL filter doesn't support nested creative.status filtering,
    // so we filter in JavaScript after fetching
    const creatives = data.studioCreativesByStudioID.items
      .filter(item => {
        // Only include items where creative exists and status is PUBLISHED
        return item?.creative?.status === 'PUBLISHED';
      })
      .map(item => {
        const processedItem = { ...item };

        // Process creative profile picture
        if (processedItem.creative?.profilePicture) {
          let processedProfilePicture = "";
          try {
            processedProfilePicture = getCloudFrontImageUrl(processedItem.creative.profilePicture, {
              width: 300,
              height: 300,
              fit: 'cover'
            });
          } catch (error) {
            processedProfilePicture = "";
          }
          processedItem.creative = {
            ...processedItem.creative,
            processedProfilePicture
          };
        }

        // Process creative user profile picture
        if (processedItem.creative?.user?.profilePicture) {
          let processedUserPicture = "";
          try {
            processedUserPicture = getCloudFrontImageUrl(processedItem.creative.user.profilePicture, {
              width: 200,
              height: 200,
              fit: 'cover'
            });
          } catch (error) {
            processedUserPicture = "";
          }
          processedItem.creative.user = {
            ...processedItem.creative.user,
            processedProfilePicture: processedUserPicture
          };
        }

        // Process portfolio images in packages
        if (processedItem.creative?.packages?.items) {
          processedItem.creative.packages = {
            ...processedItem.creative.packages,
            items: processedItem.creative.packages.items.map(pkg => {
              const processedPkg = { ...pkg };
              if (pkg.portfolioImages && Array.isArray(pkg.portfolioImages)) {
                processedPkg.processedPortfolioImages = pkg.portfolioImages.map(imageKey => {
                  if (!imageKey) return '';
                  try {
                    return getCloudFrontImageUrl(imageKey, {
                      width: 800,
                      height: 600,
                      fit: 'cover'
                    });
                  } catch (error) {
                    try {
                      const cleanKey = imageKey.startsWith('public/') ? imageKey : `public/${imageKey}`;
                      return `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                    } catch (fallbackError) {
                      return '';
                    }
                  }
                });
              }
              return processedPkg;
            })
          };
        }

        // Process creative projects (images + audio) similar to useCreativesByUserId
        if (processedItem.creative?.projects?.items) {
          const allProcessedImages = [];
          const allProcessedAudios = [];

          processedItem.creative.projects = {
            ...processedItem.creative.projects,
            items: processedItem.creative.projects.items.map(project => {
              const processedProject = { ...project };

              // Images
              if (project.images && Array.isArray(project.images)) {
                processedProject.processedImages = project.images.map(img => {
                  if (typeof img === 'string') {
                    try {
                      const processedUrl = getCloudFrontImageUrl(img, {
                        width: 1200,
                        height: 800,
                        fit: 'cover'
                      });
                      const processedImg = { url: img, processedUrl };
                      allProcessedImages.push(processedImg);
                      return processedUrl;
                    } catch (error) {
                      const processedImg = { url: img, processedUrl: img };
                      allProcessedImages.push(processedImg);
                      return img;
                    }
                  } else if (img?.url) {
                    const processedImg = { ...img };
                    let processedUrl = "";
                    try {
                      processedUrl = getCloudFrontImageUrl(img.url, {
                        width: 800,
                        height: 600,
                        fit: 'cover'
                      });
                    } catch (error) {
                      try {
                        const cleanKey = img.url.startsWith('public/') ? img.url : `public/${img.url}`;
                        processedUrl = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                      } catch (fallbackError) {
                        processedUrl = img.url;
                      }
                    }
                    processedImg.processedUrl = processedUrl;

                    // If a thumbnail exists (YouTube or uploaded video), also generate a processedThumbnailUrl
                    if (img.thumbnailUrl) {
                      try {
                        processedImg.processedThumbnailUrl = getCloudFrontImageUrl(img.thumbnailUrl, {
                          width: 400,
                          height: 300,
                          fit: 'cover'
                        });
                      } catch (error) {
                        processedImg.processedThumbnailUrl = img.thumbnailUrl;
                      }
                    }

                    allProcessedImages.push(processedImg);
                    return processedImg;
                  }
                  return img;
                });
              }

              // Audio (for MUSIC projects)
              if (project.audios && Array.isArray(project.audios)) {
                processedProject.audios = project.audios.map(audio => {
                  const processedAudio = { ...audio };
                  if (audio.url) {
                    let cleanKey = audio.url;
                    while (cleanKey.startsWith('public/public/')) {
                      cleanKey = cleanKey.replace('public/public/', 'public/');
                    }
                    if (!cleanKey.startsWith('public/')) {
                      cleanKey = `public/${cleanKey}`;
                    }
                    processedAudio.audioKey = cleanKey;
                    processedAudio.originalUrl = audio.url;
                  }
                  if (audio.coverImage) {
                    try {
                      processedAudio.processedCoverImage = getCloudFrontImageUrl(audio.coverImage, {
                        width: 600,
                        height: 600,
                        fit: 'cover'
                      });
                    } catch (error) {
                      try {
                        const cleanKey = audio.coverImage.startsWith('public/')
                          ? audio.coverImage
                          : `public/${audio.coverImage}`;
                        processedAudio.processedCoverImage = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                      } catch (fallbackError) {
                        processedAudio.processedCoverImage = audio.coverImage;
                      }
                    }
                  }
                  if (processedItem.creative?.creativeType === 'MUSIC') {
                    allProcessedAudios.push(processedAudio);
                  }
                  return processedAudio;
                });
              }

              return processedProject;
            })
          };

          processedItem.creative.allProcessedProjectImages = allProcessedImages;
          if (processedItem.creative?.creativeType === 'MUSIC') {
            processedItem.creative.allProcessedProjectAudios = allProcessedAudios;
          }
        }
        return processedItem;
      });

    return creatives;
  }, [data]);

  return {
    creatives: processedData || [],
    loading,
    error
  };
};

